import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['wrapper', 'input', 'icon', 'helper', 'asset', 'hidden', 'asterisk'];
  static values = {
    hasMask: Boolean,
    maskType: String,
    maskPattern: String,
    maskOptions: Object,
    required: Boolean,
    requiredWhen: Array,
    frontendValidation: { type: Boolean, default: false },
    validationMessage: String,
  };

  connect() {
    const hasError = this.inputTarget.dataset.hasError === 'true';
    if (hasError) this.showError();

    this._maskHandler = null;
    this._initMask();

    if (this.maskOptionsValue.formatOnConnect !== false) {
      if (this._maskHandler) this._maskHandler();
      this._syncHiddenRaw();
    }

    // initial when passed to partial
    if (this.hasRequiredValue) this.setRequired(this.requiredValue);

    // listener change
    this._onSetRequired = (e) => {
      const state = !!(e.detail && e.detail.required);
      this.setRequired(state);
    };
    this.element.addEventListener('input:set-required', this._onSetRequired);
    this.element.removeEventListener('input:set-required', this._onSetRequired);

    if (this.frontendValidationValue) {
      this.addValidationListener();
    }
  }

  addValidationListener() {
    this.inputTarget.addEventListener('invalid', (event) => {
      event.preventDefault();
      this.showValidationError();
    });
  }

  showValidationError() {
    this.showError();

    const message = this.validationMessageValue || this.inputTarget.validationMessage;

    if (!this.hasHelperTarget) {
      const helperText = document.createElement('p');
      helperText.dataset.inputTarget = 'helper';
      helperText.className = 'mt-8 text-paragraph-s text-red-1000';
      helperText.textContent = message;

      this.element.appendChild(helperText);
    } else {
      if (this.helperTarget.dataset.backendError !== 'true') {
        this.helperTarget.textContent = message;
      }
      this.helperTarget.style.display = '';
      this.helperTarget.classList.add('text-red-1000');
    }
  }

  clearValidationError() {
    if (this.wrapperTarget) {
      this.wrapperTarget.classList.remove('border-red-300', 'has-error');
      this.wrapperTarget.classList.add('border-black-100');
    }

    this.inputTarget.classList.remove(
      'text-red-1000',
      'placeholder-red-1000',
      'border-red-300',
      'border-l',
      'border-l-red-300',
    );

    this.iconTargets.forEach((icon) => {
      icon.classList.remove('text-red-1000');
    });

    if (this.hasHelperTarget && !this.helperTarget.dataset.backendError) {
      this.helperTarget.style.display = 'none';
    }
  }

  disconnect() {
    this._maskHandler = null;
  }

  showError() {
    if (this.wrapperTarget) {
      this.wrapperTarget.classList.add('border-red-300');
      this.wrapperTarget.classList.add('has-error');
    }

    if (this.hasHelperTarget) {
      this.helperTarget.classList.add('text-red-1000');
    }

    this.inputTarget.classList.add(
      'text-red-1000',
      'placeholder-red-1000',
      'border-red-300',
      'border-l',
      'border-l-red-300',
    );

    this.iconTargets.forEach((icon) => {
      icon.classList.add('text-red-1000');
    });
  }

  focus() {
    this.wrapperTarget.classList.add(
      'ring-2',
      'ring-black-100',
      'ring-offset-2',
      'ring-offset-white',
    );
  }

  blur() {
    this.wrapperTarget.classList.remove(
      'ring-2',
      'ring-black-100',
      'ring-offset-2',
      'ring-offset-white',
    );
  }

  input(event) {
    if (this._maskHandler) this._maskHandler(event);
    this._syncHiddenRaw();
    if (this.frontendValidationValue) {
      this.clearValidationError();
    }
  }

  // ============= masks =============

  _initMask() {
    // 1) If a specific pattern is provided and no mask type is set, use the pattern
    if (this.maskPatternValue && !this.maskTypeValue) {
      this._enableSimplePatternMask(this.maskPatternValue);
      return;
    }
    const type = (this.maskTypeValue || '').toLowerCase();

    switch (type) {
      // — UK —
      case 'phone-uk':
        this._applyRecommendedInputMode('tel');
        this._maskHandler = () => this._formatUKPhone();
        break;

      case 'postal-uk':
        this._applyRecommendedInputMode('text');
        this._maskHandler = () => this._formatUKPostcode();
        break;

      case 'date-uk':
        this._applyRecommendedInputMode('numeric');
        this._enableSimplePatternMask('00/00/0000'); // dd/mm/yyyy
        return;

      case 'sort-code-uk':
        this._applyRecommendedInputMode('numeric');
        this._maskHandler = () => this._formatUKSortCode();
        break;

      case 'ni-uk':
        this._applyRecommendedInputMode('text');
        this._maskHandler = () => this._formatUKNINumber();
        break;

      case 'nhs-uk':
        this._applyRecommendedInputMode('numeric');
        this._maskHandler = () => this._formatUKNHSNumber();
        break;

       // — numeric (custom) —
      case 'numeric': // integer without separators
        this._applyRecommendedInputMode('numeric');
        this._maskHandler = () => this._formatNumericPlain();
        break;

      case 'numeric-with-separators': // integer with thousands separators
        this._applyRecommendedInputMode('numeric');
        this._maskHandler = () => this._formatNumericWithSeparators();
        break;

      case 'project-currency': // "1,234,567.89" – always show decimals on initial value
        this._applyRecommendedInputMode('decimal');
        this._maskHandler = (event) => this._formatProjectCurrency(event);
        break;

      case 'decimal-optional': // "1,234,567" or "1,234,567.89" – optional decimals, no forced .00
        this._applyRecommendedInputMode('decimal');
        this._maskHandler = (event) => this._formatDecimalOptional(event);
        break;

      case 'vp-number': // "123,123"
        this._applyRecommendedInputMode('decimal');
        this._maskHandler = () => this._formatVPNumber();
        break;

      default:
        return;
    }
  }

  _applyRecommendedInputMode(mode) {
    if (!this.inputTarget.getAttribute('inputmode')) {
      this.inputTarget.setAttribute('inputmode', mode);
    }
  }

  _enableSimplePatternMask(pattern) {
    this._maskHandler = () => {
      const digits = (this.inputTarget.value || '').replace(/\D/g, '');
      let out = '';
      let d = 0;

      for (let i = 0; i < pattern.length; i++) {
        const p = pattern[i];
        if (p === '0') {
          if (d < digits.length) out += digits[d++];
          else break;
        } else {
          out += p;
        }
      }
      this.inputTarget.value = out;
    };
  }

  // ===== UK formatters =====
  _formatUKPhone() {
    const raw = (this.inputTarget.value || '').replace(/[^\d+]/g, '');
    if (!raw) {
      this.inputTarget.value = '';
      return;
    }

    let digits = raw;
    if (digits.startsWith('+44')) {
      digits = digits.replace('+44', '');
    } else if (digits.startsWith('44')) {
      digits = digits.slice(2);
    } else if (digits.startsWith('0')) {
      digits = digits.slice(1);
    }
    digits = digits.replace(/\D/g, '').slice(0, 10); // after +44 max 10 digits

    if (!digits) {
      this.inputTarget.value = '';
      return;
    }

    let out = '+44 ';
    if (digits.startsWith('7')) {
      // mobile: +44 7xxx xxxxxx (4-6)
      const a = digits.slice(0, 4);
      const b = digits.slice(4, 10);
      out += a + (b ? ' ' + b : '');
    } else if (digits.startsWith('20')) {
      // London: +44 20 xxxx xxxx (2-4-4)
      const a = digits.slice(0, 2);
      const b = digits.slice(2, 6);
      const c = digits.slice(6, 10);
      out += a + (b ? ' ' + b : '') + (c ? ' ' + c : '');
    } else {
      // default 4-6
      const a = digits.slice(0, 4);
      const b = digits.slice(4, 10);
      out += a + (b ? ' ' + b : '');
    }

    this.inputTarget.value = out.trim();
  }

  _formatUKPostcode() {
    let v = (this.inputTarget.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

    if (v.length <= 3) {
      this.inputTarget.value = v;
      return;
    }

    const outward = v.slice(0, v.length - 3);
    const inward = v.slice(-3);
    this.inputTarget.value = `${outward} ${inward}`.slice(0, 8); // max "AAAA 9AA"
  }

  _formatUKSortCode() {
    const d = (this.inputTarget.value || '').replace(/\D/g, '').slice(0, 6);
    let out = '';
    if (d.length <= 2) out = d;
    else if (d.length <= 4) out = `${d.slice(0, 2)}-${d.slice(2)}`;
    else out = `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
    this.inputTarget.value = out;
  }

  _formatUKNINumber() {
    let v = (this.inputTarget.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    v = v.slice(0, 9); // 2L + 6D + 1L

    const p1 = v.slice(0, 2).replace(/[^A-Z]/g, '');
    const p2 = v.slice(2, 4).replace(/\D/g, '');
    const p3 = v.slice(4, 6).replace(/\D/g, '');
    const p4 = v.slice(6, 8).replace(/\D/g, '');
    const p5 = v.slice(8, 9).replace(/[^A-Z]/g, '');

    const out = [p1, p2, p3, p4].filter(Boolean).join(' ') + (p5 ? ' ' + p5 : '');
    this.inputTarget.value = out.trim();
  }

  _formatUKNHSNumber() {
    const d = (this.inputTarget.value || '').replace(/\D/g, '').slice(0, 10);
    const a = d.slice(0, 3);
    const b = d.slice(3, 6);
    const c = d.slice(6, 10);
    this.inputTarget.value = [a, b, c].filter(Boolean).join(' ').trim();
  }

  // ===== numeric (custom) =====
  _formatNumericPlain() {
    const el = this.inputTarget;
    const raw = el.value || '';

    const integerPart = raw.split(/[.,]/, 1)[0] || '';

    let v = integerPart.replace(/\D/g, '');

    if (v.length > 1) v = v.replace(/^0+/, '');

    el.value = v;
  }

  _formatNumericWithSeparators() {
    const el = this.inputTarget;
    const prevValue = el.value || '';
    const caretPos = el.selectionStart;

    // only consider the integer part before any decimal separator
    const prevIntegerPart = prevValue.split('.', 1)[0] || '';

    // collect digits only from the integer part
    let digits = prevIntegerPart.replace(/\D/g, '');

    if (!digits.length) {
      el.value = '';
      return;
    }

    // remove leading zeros, but keep a single "0"
    digits = digits.replace(/^0+(?=\d)/, '');

    // apply thousands separators
    const nextValue = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    el.value = nextValue;

    // caret handling – keep the same count of digits before caret (within integer part)
    if (
      document.activeElement === el &&
      typeof caretPos === 'number' &&
      caretPos !== null
    ) {
      const integerBeforeCaret = prevIntegerPart.slice(0, Math.min(caretPos, prevIntegerPart.length));
      const digitsBefore = integerBeforeCaret.replace(/\D/g, '').length;

      let newCaret = nextValue.length;

      if (digitsBefore > 0) {
        let count = 0;
        for (let i = 0; i < nextValue.length; i++) {
          if (/\d/.test(nextValue[i])) {
            count++;
            if (count === digitsBefore) {
              newCaret = i + 1;
              break;
            }
          }
        }
      }

      try {
        el.setSelectionRange(newCaret, newCaret);
      } catch (e) {
        // ignore
      }
    }
  }

  // "1,234,567.89" — UK: comma thousands, dot decimal, max 2 places
  _formatProjectCurrency(event) {
    const el = this.inputTarget;
    const prevValue = el.value || '';
    const caretPos = el.selectionStart;

    const typedDot = event && event.data === '.';
    const isInitialCall = !event; // true when called from connect() without DOM event

    let raw = prevValue;
    const endsWithDot = /\.$/.test(raw);

    // strip commas/spaces, leave digits + dot
    raw = raw.replace(/[,\s]/g, '').replace(/[^0-9.]/g, '');

    if (!raw) {
      el.value = '';
      return;
    }

    const firstDot = raw.indexOf('.');
    let intRaw;
    let fracRaw;

    if (firstDot === -1) {
      intRaw = raw;
      fracRaw = '';
    } else {
      intRaw = raw.slice(0, firstDot);
      fracRaw = raw.slice(firstDot + 1);
    }

    intRaw = intRaw.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    fracRaw = fracRaw.replace(/\D/g, '').slice(0, 2);

    // thousands separators
    const intOut = intRaw.length
      ? intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : '0';

    // ---- INITIAL VALUE NORMALIZATION ----
    if (isInitialCall) {
      // always exactly 2 decimal places on initial value
      const normalizedFrac = (fracRaw + '00').slice(0, 2); // "", "1", "12" -> "00", "10", "12"
      const nextValue = `${intOut}.${normalizedFrac}`;
      el.value = nextValue;
      return;
    }

    // ---- NORMAL TYPING BEHAVIOUR (unchanged) ----
    let nextValue;

    // allow "123." while user is about to type decimals
    if (endsWithDot && !fracRaw.length) {
      nextValue = `${intOut}.`;
    } else if (fracRaw.length) {
      // keep decimals (max 2)
      nextValue = `${intOut}.${fracRaw}`;
    } else {
      // pure integer
      nextValue = intOut;
    }

    el.value = nextValue;

    // caret handling
    if (
      document.activeElement === el &&
      typeof caretPos === 'number' &&
      caretPos !== null
    ) {
      // when user just typed '.', put caret at the end
      if (typedDot) {
        const end = nextValue.length;
        try {
          el.setSelectionRange(end, end);
        } catch (e) {}
        return;
      }

      const digitsBefore = prevValue
        .slice(0, caretPos)
        .replace(/\D/g, '').length;

      let newCaret = nextValue.length;
      if (digitsBefore > 0) {
        let count = 0;
        for (let i = 0; i < nextValue.length; i++) {
          if (/\d/.test(nextValue[i])) {
            count++;
            if (count === digitsBefore) {
              newCaret = i + 1;
              break;
            }
          }
        }
      }

      try {
        el.setSelectionRange(newCaret, newCaret);
      } catch (e) {
        // ignore
      }
    }
  }

  // "1,234,567" or "1,234,567.89" — optional decimals, no forced .00 on initial value
  _formatDecimalOptional(event) {
    const el = this.inputTarget;
    const prevValue = el.value || '';
    const caretPos = el.selectionStart;

    const typedDot = event && event.data === '.';
    const isInitialCall = !event;

    let raw = prevValue;
    const endsWithDot = /\.$/.test(raw);

    // strip commas/spaces, leave digits + dot
    raw = raw.replace(/[,\s]/g, '').replace(/[^0-9.]/g, '');

    if (!raw) {
      el.value = '';
      return;
    }

    const firstDot = raw.indexOf('.');
    let intRaw;
    let fracRaw;

    if (firstDot === -1) {
      intRaw = raw;
      fracRaw = '';
    } else {
      intRaw = raw.slice(0, firstDot);
      fracRaw = raw.slice(firstDot + 1);
    }

    intRaw = intRaw.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    fracRaw = fracRaw.replace(/\D/g, '').slice(0, 2); // max 2 decimal places

    const intOut = intRaw.length
      ? intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : '0';

    const hasNonZeroFraction = /[1-9]/.test(fracRaw);

    let nextValue;

    // On initial call: if fraction is zero/empty → show pure integer (no decimals)
    if (isInitialCall && !hasNonZeroFraction) {
      nextValue = intOut;
    } else if (endsWithDot && !fracRaw.length) {
      // allow "123." while user is typing decimals
      nextValue = `${intOut}.`;
    } else if (fracRaw.length) {
      nextValue = `${intOut}.${fracRaw}`;
    } else {
      nextValue = intOut;
    }

    el.value = nextValue;

    // caret handling
    if (
      document.activeElement === el &&
      typeof caretPos === 'number' &&
      caretPos !== null
    ) {
      if (typedDot) {
        const end = nextValue.length;
        try {
          el.setSelectionRange(end, end);
        } catch (e) {}
        return;
      }

      const digitsBefore = prevValue
        .slice(0, caretPos)
        .replace(/\D/g, '').length;

      let newCaret = nextValue.length;
      if (digitsBefore > 0) {
        let count = 0;
        for (let i = 0; i < nextValue.length; i++) {
          if (/\d/.test(nextValue[i])) {
            count++;
            if (count === digitsBefore) {
              newCaret = i + 1;
              break;
            }
          }
        }
      }

      try {
        el.setSelectionRange(newCaret, newCaret);
      } catch (e) {
        // ignore
      }
    }
  }

  // "123,123" — comma decimal, 3 places, no thousands grouping
  _formatVPNumber() {
    const raw = this.inputTarget.value || '';
    const endsWithComma = /,$/.test(raw);

    let v = raw.replace(/[^\d,]/g, '').replace(/,+/g, ',');

    let [intPart, fracPart = ''] = v.split(',');
    intPart = (intPart || '').replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    fracPart = (fracPart || '').replace(/\D/g, '').slice(0, 3);

    if (endsWithComma && fracPart.length === 0) {
      this.inputTarget.value = (intPart || '0') + ',';
    } else {
      this.inputTarget.value = fracPart.length ? `${intPart},${fracPart}` : intPart;
    }
  }

  _syncHiddenRaw() {
    if (!this.hasHiddenTarget) return;

    const type = (this.maskTypeValue || '').toLowerCase();
    const v = (this.inputTarget.value || '').trim();
    let raw = '';

    switch (type) {
      case 'numeric':
      case 'numeric-with-separators':
        // integer: take only part before decimal separator and strip non-digits
        raw = v.split('.', 1)[0].replace(/\D/g, '');
        break;

      case 'project-currency':
      case 'decimal-optional':
        // decimal with dot, commas and spaces removed
        raw = v.replace(/,/g, '').replace(/\s+/g, '');
        break;

      case 'vp-number':
        raw = v.replace(/\s+/g, '').replace(',', '.');
        break;

      default:
        raw = v;
    }

    this.hiddenTarget.value = raw;
  }

  requiredFromSelect(event) {
    const val = event.target.value;
    if (this.hasRequiredWhenValue) {
      const should = this.requiredWhenValue.includes(val);
      this.setRequired(should);
      return;
    }

    const p = event.params?.required;
    if (typeof p !== 'undefined') {
      this.setRequired(p === true || p === 'true' || p === 1 || p === '1');
    }
  }

  setRequired(state) {
    const el = this.inputTarget;
    const on = !!state;

    el.toggleAttribute('required', on);
    el.setAttribute('aria-required', on ? 'true' : 'false');

    if (this.hasAsteriskTarget) {
      this.asteriskTarget.classList.toggle('hidden', !on);
    }
  }
}
